import {
  CloseOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  ShareAltOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Flex,
  Input,
  MenuProps,
  Segmented,
  Spin,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import usePatientSync from "../../hooks/usePatientSync";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { deleteClient } from "../../service/client.service";
import {
  setCreateClientState,
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
  setShareClientModalClient,
  setShareClientModalVisible,
  setShowDemoSignupModal,
} from "../../slices/appStateSlice";
import {
  type TClientSort,
  clearRecentlySyncedId,
  fetchClientSidebarPage,
  persistSortPreference,
  removeClient,
  resetClientForm,
  selectRecentlySyncedIds,
  selectSidebarSection,
  selectSidebarSort,
  setClientForm,
  setIsEditing,
  setSidebarQuery,
  setSidebarSort,
} from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import type { TClientForm } from "../../types/index.type";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import { EhrSyncButton, EhrSyncStatus, useEhrSync } from "../EhrSyncButton";
import EhrVerificationBadge from "../EhrVerificationBadge/EhrVerificationBadge.component";
import EmptyStateClient from "../EmptyState/EmptyStateClient.component";
import PatientSyncNotice from "../PatientSyncNotice/PatientSyncNotice.component";
import styles from "./clientview.module.scss";

const ClientView = ({
  demoClients,
  addClientRef,
  clientViewRef,
}: {
  demoClients?: TClientForm[];
  addClientRef?: React.RefObject<HTMLDivElement>;
  clientViewRef?: React.RefObject<HTMLDivElement>;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { referIndividualBy } = useSpecialityReference();
  const componentName = "ClientView";

  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedSegment, setSelectedSegment] = React.useState<"all" | "shared">("all");

  const { rootPath } = useRootPath();
  const { isSyncing } = usePatientSync();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { selectedClient, isDemoPath, isMobilePath } = useAppSelector((state) => state.appState);

  const { isRecording, isPaused } = useAppSelector((state) => state.recordings);

  const allSection = useAppSelector(selectSidebarSection("all"));
  const sharedSection = useAppSelector(selectSidebarSection("shared"));
  const byId = useAppSelector((state) => state.client.byId);
  const recentlySyncedIds = useAppSelector(selectRecentlySyncedIds);
  const recentlySynced = React.useMemo(() => new Set(recentlySyncedIds), [recentlySyncedIds]);

  // On-demand EHR sync. `can_sync_ehr_patients` is computed once on bootstrap and is
  // the only thing that reveals the control; the demo path lists fabricated patients,
  // so there is nothing there to sync. One hook instance feeds the toolbar button, the
  // status strip, the empty-state CTA and the "new" markers.
  const canSync = useAppSelector((state) => state.auth.userInfo.can_sync_ehr_patients);
  const sync = useEhrSync();
  const showSync = !!canSync && !demoClients && !sync.hidden;
  const syncedAtLabel = sync.lastSyncedAt ? ` at ${dayjs(sync.lastSyncedAt).format("h:mm A")}` : "";

  // Sidebar lists hydrated from the paginated server pages (demoClients short-circuits to demo data).
  const allClients = React.useMemo(
    () => (demoClients ? demoClients : (allSection.ids.map((id) => byId[id]).filter(Boolean) as TClientForm[])),
    [demoClients, allSection.ids, byId],
  );
  const sharedClientsList = React.useMemo(
    () => sharedSection.ids.map((id) => byId[id]).filter(Boolean) as TClientForm[],
    [sharedSection.ids, byId],
  );

  const selectClient = (client: TClientForm | null, showLoader = true) => {
    if (selectedClient?.id && client && client.id && selectedClient.id === client.id) {
      return;
    }
    if (showLoader) {
      dispatch(setLoadingNotesState(true));
    }
    dispatch(setSelectedClient(client));
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const handleClientSelectWithNavigate = (client: TClientForm) => {
    // Opening a patient the last sync added is what retires its "new" marker.
    if (client.id != null && recentlySynced.has(client.id)) dispatch(clearRecentlySyncedId(client.id));
    selectClient(client);
    navigate(`${rootPath}/clients/${client.uuid}`);
  };

  const clearSelection = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    navigate(rootPath);
    selectClient(null);
  };

  const showClientView = () => {
    dispatch(setCreateClientState(true));
    dispatch(setIsEditing(false));
    dispatch(resetClientForm());
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const sortBy = useAppSelector(selectSidebarSort);

  // Load (and reload on search or sort change) the all + shared sidebar pages, debounced.
  // The thunk reads sortBy from state, so listing sortBy here just triggers the refetch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: searchTerm/sortBy-driven server fetch
  React.useEffect(() => {
    if (demoClients) return;
    const timeout = setTimeout(() => {
      dispatch(setSidebarQuery(searchTerm));
      dispatch(fetchClientSidebarPage({ scope: "all", offset: 0, search: searchTerm, reset: true }));
      dispatch(fetchClientSidebarPage({ scope: "shared", offset: 0, search: searchTerm, reset: true }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, sortBy, demoClients, dispatch]);

  const handleSortChange = (next: TClientSort) => {
    if (next === sortBy) return;
    persistSortPreference(next);
    dispatch(setSidebarSort(next)); // the effect above refetches both sections
  };

  // Infinite scroll: load the next page of the visible section as the list nears the bottom.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (demoClients) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) return;
    const section = selectedSegment === "shared" ? sharedSection : allSection;
    const scope = selectedSegment === "shared" ? "shared" : "all";
    if (section.hasMore && !section.loading) {
      dispatch(fetchClientSidebarPage({ scope, offset: section.offset, search: searchTerm, reset: false }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleMenu = (e: any) => {
    e.domEvent.stopPropagation();
    if (e.key === "1") {
      dispatch(setCreateClientState(true));
      if (selectedClient) dispatch(setClientForm(selectedClient));
      dispatch(setIsEditing(true));
    }
    if (e.key === "2") {
      // Share action — group users only; everyone else gets a toast, not the popup
      if (selectedClient) {
        if (!userInfo?.group_name) {
          message.error(`Sharing ${referIndividualBy.toLowerCase()}s is only available for group accounts`);
          return;
        }
        dispatch(setShareClientModalClient(selectedClient));
        dispatch(setShareClientModalVisible(true));
      }
    }
    if (e.key === "3") {
      if (isDemoPath) {
        dispatch(setShowDemoSignupModal(true));
        return;
      }
      Swal.fire({
        title: `Would you like to delete this ${referIndividualBy.toLowerCase()}?`,
        text: `The ${referIndividualBy.toLowerCase()}'s notes will still be available unless you delete them.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      }).then((result) => {
        if (result.isConfirmed && selectedClient && selectedClient.id) {
          deleteClient(selectedClient.id)
            .then(() => {
              navigate(rootPath);
              dispatch(removeClient(selectedClient.id as number));
              dispatch(setSelectedClient(null));
              message.success(`${referIndividualBy} deleted successfully`);
            })
            .catch((error) => {
              console.error(error);
              message.error(`Failed to delete ${referIndividualBy.toLowerCase()}.`);
            });
        }
      });
    }
  };

  // Menu items - show edit for editable clients, share/delete only for owned clients
  const isOwnedClient = selectedClient?.is_owned !== false; // undefined or true means owned
  const canEditClient = selectedClient?.can_edit !== false; // undefined or true means can edit

  const items: MenuProps["items"] = (() => {
    const menuItems: MenuProps["items"] = [];
    if (canEditClient && !isMobilePath) {
      menuItems.push({
        key: "1",
        label: <span>Edit {referIndividualBy}</span>,
      });
    }
    if (isOwnedClient && !isMobilePath) {
      menuItems.push({
        key: "2",
        label: <span>Share {referIndividualBy}</span>,
      });
    }
    if (isOwnedClient) {
      menuItems.push({
        key: "3",
        label: <span>Delete {referIndividualBy}</span>,
      });
    }
    return menuItems;
  })();

  // Active segment's list + pagination section, driving the infinite-scroll footer states below.
  const activeList = selectedSegment === "shared" ? sharedClientsList : allClients;
  const activeSection = selectedSegment === "shared" ? sharedSection : allSection;
  const activeScope = selectedSegment === "shared" ? "shared" : "all";
  // First page still loading (mount or a fresh search) — show a loader, not the "no patients" empty state.
  const isInitialLoading = !demoClients && activeSection.loading && activeList.length === 0;
  // First page failed outright — show an error + retry, never the "no patients" empty state (a disguised error).
  const isInitialError = !demoClients && activeSection.error && activeList.length === 0;
  const retryLoadMore = () =>
    dispatch(
      fetchClientSidebarPage({ scope: activeScope, offset: activeSection.offset, search: searchTerm, reset: false }),
    );

  const renderClientRow = (data: TClientForm, index: number) => {
    const isSelected = !!selectedClient && selectedClient.id === data.id;
    const isNewFromSync = data.id != null && recentlySynced.has(data.id);
    const isShared = data.is_owned === false && !!data.owner_name;
    const sharedBy = isShared ? `Shared by ${data.owner_name}` : "";

    // Calculate inline details for MRN/PATIENT ID and DOB
    const formattedDob = data.date_of_birth ? dayjs(data.date_of_birth).format("MM/DD/YYYY") : "";
    const derivedAgeFromDob =
      data.date_of_birth && formattedDob ? dayjs().diff(dayjs(data.date_of_birth), "year") : null;
    const rawAge = typeof data.age === "number" ? data.age : data.age ? Number.parseInt(data.age.toString(), 10) : null;
    const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

    const inlineDetails: string[] = [];
    if (formattedDob) {
      inlineDetails.push(formattedDob);
    } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
      inlineDetails.push(`Age ${displayAge}`);
    }
    if (data.mrn) {
      inlineDetails.push(`MRN/Patient ID: ${data.mrn}`);
    }

    return (
      <Flex
        key={data.id ?? index}
        gap={10}
        align="center"
        justify="space-between"
        className={`${styles[`${componentName}__container-client`]} ${
          isSelected ? styles[`${componentName}__container-client-selected`] : ""
        } ${isNewFromSync ? styles[`${componentName}__container-client-synced`] : ""}`}
        onClick={() => handleClientSelectWithNavigate(data)}
      >
        <Flex align="center" gap={10} style={{ flex: 1, minWidth: 0 }}>
          {/* Rows the last sync added carry an indigo dot until opened — an icon-with-tooltip
              adornment like the MRN warning and the shared marker, in the sidebar's own palette. */}
          <Tooltip placement="top" title={isNewFromSync ? `Added by sync${syncedAtLabel}` : ""}>
            <Badge dot={isNewFromSync} color="var(--indigo-500)" offset={[-2, 3]}>
              <Avatar
                size={"small"}
                className="special-avatar"
                style={{
                  backgroundColor: getRandomAvatarColor(data.name),
                  minWidth: "24px",
                }}
              >
                {data.name.charAt(0).toUpperCase()}
              </Avatar>
            </Badge>
          </Tooltip>
          <Flex vertical style={{ flex: 1, minWidth: 0 }}>
            <Flex align="center" gap={5} style={{ minWidth: 0 }}>
              <Typography className={`${styles[`${componentName}__container-client-name`]}`}>{data.name}</Typography>
              <EhrVerificationBadge verification={data.ehr_verification} />
              {isShared && (
                <Tooltip placement="top" title={sharedBy}>
                  <svg
                    role="img"
                    aria-label="Shared patient"
                    style={{ flexShrink: 0 }}
                    width="14"
                    height="11"
                    viewBox="0 0 14 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.41792 2.5C7.41792 3.16304 7.15453 3.79893 6.68569 4.26777C6.21685 4.73661 5.58096 5 4.91792 5C4.25488 5 3.61899 4.73661 3.15015 4.26777C2.68131 3.79893 2.41792 3.16304 2.41792 2.5C2.41792 1.83696 2.68131 1.20107 3.15015 0.732233C3.61899 0.263392 4.25488 0 4.91792 0C5.58096 0 6.21685 0.263392 6.68569 0.732233C7.15453 1.20107 7.41792 1.83696 7.41792 2.5ZM9.81792 10.006C9.92792 10.548 9.46992 11 8.91792 11H0.917919C0.364919 11 -0.0920809 10.548 0.0159191 10.006C0.245969 8.87575 0.859558 7.85974 1.75279 7.13C2.64602 6.40027 3.764 6.00165 4.91742 6.00165C6.07084 6.00165 7.18882 6.40027 8.08205 7.13C8.97528 7.85974 9.58887 8.87575 9.81892 10.006H9.81792ZM12.9199 10H11.3299C11.3221 9.90265 11.3088 9.80582 11.2899 9.71C11.0992 8.76638 10.7006 7.87714 10.1229 7.107C10.8579 6.9047 11.6424 6.98961 12.3171 7.34451C12.9918 7.69941 13.5062 8.29771 13.7559 9.018C13.9359 9.54 13.4729 10 12.9199 10ZM10.9179 6C11.4484 6 11.9571 5.78929 12.3321 5.41421C12.7072 5.03914 12.9179 4.53043 12.9179 4C12.9179 3.46957 12.7072 2.96086 12.3321 2.58579C11.9571 2.21071 11.4484 2 10.9179 2C10.3875 2 9.87878 2.21071 9.50371 2.58579C9.12863 2.96086 8.91792 3.46957 8.91792 4C8.91792 4.53043 9.12863 5.03914 9.50371 5.41421C9.87878 5.78929 10.3875 6 10.9179 6Z"
                      fill="#9F9F9F"
                    />
                  </svg>
                </Tooltip>
              )}
            </Flex>
            {isShared && (
              <Typography className={`${styles[`${componentName}__container-client-shared-by`]}`}>
                {sharedBy}
              </Typography>
            )}
            {isSelected && inlineDetails.length > 0 && (
              <Typography className={`${styles[`${componentName}__container-client-details`]}`}>
                {inlineDetails.join(" · ")}
              </Typography>
            )}
          </Flex>
        </Flex>
        <Flex gap={10}>
          {!isShared && (
            <ShareAltOutlined
              style={{ color: selectedClient ? "var(--indigo-700)" : "var(--grey-text)" }}
              onClick={(e) => {
                if (isSelected) {
                  // Group users only — everyone else gets a toast, not the popup
                  if (userInfo?.group_name) {
                    e.stopPropagation();
                    dispatch(setShareClientModalClient(data));
                    dispatch(setShareClientModalVisible(true));
                  } else {
                    message.error(`Sharing ${referIndividualBy.toLowerCase()}s is only available for group accounts`);
                  }
                }
              }}
            />
          )}
          {isSelected && (
            <>
              <CloseOutlined style={{ color: "var(--indigo-700)" }} onClick={clearSelection} />
              {items && items.length > 0 && (
                <Dropdown menu={{ items, onClick: handleMenu }} placement="bottomLeft" arrow trigger={["click"]}>
                  <MoreOutlined style={{ color: "var(--indigo-700)" }} />
                </Dropdown>
              )}
            </>
          )}
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex
      vertical
      gap={10}
      className={
        !isRecording && !isPaused
          ? styles[`${componentName}__container`]
          : isRecording
            ? styles[`${componentName}__container-active-recording`]
            : isPaused
              ? styles[`${componentName}__container-paused-recording`]
              : ""
      }
      ref={clientViewRef}
    >
      <Flex justify="space-between" align="center" gap={8} className={styles[`${componentName}__container-header`]}>
        {/* <Typography className={styles[`${componentName}__container-title`]}>{referIndividualBy}s</Typography> */}
        <Segmented
          className="client-segmented"
          options={[
            {
              label: `All ${referIndividualBy}s`,
              value: "all",
            },
            {
              label: "Shared With Me",
              value: "shared",
            },
          ]}
          value={selectedSegment}
          onChange={(value) => {
            setSelectedSegment(value as "all" | "shared");
            setSearchTerm(""); // Clear search when switching segments
          }}
          block
        />
        <Button
          className="secondary-button"
          type="primary"
          size="middle"
          icon={<PlusOutlined />}
          onClick={showClientView}
          style={{ padding: "0 10px" }}
        >
          Add
        </Button>
      </Flex>
      {(allClients.length > 0 || sharedClientsList.length > 0 || searchTerm !== "") && (
        <>
          <Flex gap={6}>
            <Input
              value={searchTerm}
              prefix={<SearchOutlined />}
              placeholder={`Search ${referIndividualBy}s`}
              onChange={handleInputChange}
              allowClear
              style={{ padding: "5px 10px", flex: 1 }}
            />
            {/* Sort toggle for the shared browse list (sidebar + every picker, one source). */}
            <Dropdown
              trigger={["click"]}
              menu={{
                selectable: true,
                selectedKeys: [sortBy],
                items: [
                  { key: "name", label: "Alphabetical (A–Z)" },
                  { key: "recent", label: "Recently added" },
                ],
                onClick: ({ key }) => handleSortChange(key as TClientSort),
              }}
            >
              <Button
                icon={<SortAscendingOutlined />}
                title={`Sort: ${sortBy === "recent" ? "recently added" : "alphabetical"}`}
                aria-label="Sort patients"
              />
            </Dropdown>
            {showSync && <EhrSyncButton sync={sync} />}
          </Flex>
        </>
      )}
      {showSync && <EhrSyncStatus sync={sync} />}
      {
        <Flex
          vertical
          onScroll={handleScroll}
          className={styles[`${componentName}__container-scroll${activeList.length === 0 ? "-empty" : ""}`]}
        >
          {activeList.length > 0 ? (
            <>
              {activeList.map(renderClientRow)}
              {/* Infinite-scroll footer: loading-more spinner, or load-more error + retry. End of list shows nothing. */}
              {activeSection.loading && (
                <Flex align="center" justify="center" gap={8} style={{ padding: "12px 0" }} aria-hidden="true">
                  <Spin size="small" />
                  <Typography style={{ color: "var(--grey-text)" }}>Loading more…</Typography>
                </Flex>
              )}
              {!activeSection.loading && activeSection.error && (
                <Flex align="center" justify="center" gap={6} style={{ padding: "12px 0" }}>
                  <Typography style={{ color: "#cf1322" }}>⚠ Couldn't load more.</Typography>
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={retryLoadMore}>
                    Retry
                  </Button>
                </Flex>
              )}
            </>
          ) : isInitialLoading ? (
            <Flex align="center" justify="center" style={{ padding: "24px 0" }} aria-hidden="true">
              <Spin size="small" />
            </Flex>
          ) : isInitialError ? (
            <Flex vertical align="center" justify="center" gap={6} style={{ padding: "24px 0" }}>
              <Typography style={{ color: "#cf1322" }}>⚠ Couldn't load patients.</Typography>
              <Button type="link" size="small" onClick={retryLoadMore}>
                Retry
              </Button>
            </Flex>
          ) : isSyncing && selectedSegment === "all" && !searchTerm ? (
            <PatientSyncNotice />
          ) : (
            <EmptyStateClient
              segment={selectedSegment}
              onSyncFromEhr={showSync ? sync.startSync : undefined}
              syncing={sync.phase === "running"}
            />
          )}
          {/* Screen-reader mirror of the scroll/footer status (visual users get the spinner/retry above). */}
          <div
            aria-live="polite"
            role="status"
            style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}
          >
            {activeSection.loading
              ? activeList.length > 0
                ? "Loading more patients"
                : "Loading patients"
              : activeSection.error
                ? activeList.length > 0
                  ? "Couldn't load more patients"
                  : "Couldn't load patients"
                : ""}
          </div>
        </Flex>
      }
    </Flex>
  );
};

export default ClientView;
